import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/browser';

import { API_URL, APP_VERSION } from '@/constants';
import { getTokens, removeToken } from '@/stores/secure';
import { useAuthStore } from '@/stores/AuthState';
import { AUTH_ROUTE } from '@/constants/routes';
import { refreshAndStoreTokens } from './refreshToken';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
    _retryCount?: number;
  }
}

/**
 * Axios layer — web port of the legacy `api/config.ts` (~95% reused per the
 * migration doc). Two instances:
 *  - `cleanAPI`  — no auth headers, no interceptors (login / callback / refresh)
 *  - `api`       — authenticated, with a single-flight 401 → refresh → retry
 *
 * Platform detection uses `Capacitor.getPlatform()` (not `Platform.OS`), and
 * the version comes from package.json via the `__APP_VERSION__` Vite define.
 */

// ─── Force logout (hard 401 / unrecoverable refresh failure) ─────────────────
let isLoggingOut = false;

export async function forceLogout(reason: string): Promise<void> {
  if (isLoggingOut) return;
  isLoggingOut = true;
  try {
    if (import.meta.env.DEV) {
      console.warn('[auth] forceLogout:', reason);
    }
    await removeToken();
    await useAuthStore.getState().logout();
  } finally {
    // Route back to login. Full reload keeps state clean across the WebView.
    if (typeof window !== 'undefined' && window.location.pathname !== AUTH_ROUTE) {
      window.location.assign(AUTH_ROUTE);
    }
    isLoggingOut = false;
  }
}

// ─── Common headers ──────────────────────────────────────────────────────────
const setCommonHeaders = async (config: InternalAxiosRequestConfig) => {
  const { userInfo } = useAuthStore.getState();
  const { bearerToken } = await getTokens();

  config.baseURL = API_URL;
  config.headers['x-platform-type'] = Capacitor.isNativePlatform() ? 'mobile' : 'web';
  config.headers['x-platform-os'] = Capacitor.getPlatform();
  config.headers['x-app-version'] = APP_VERSION;

  if (userInfo) {
    if (userInfo.service) config.headers['x-external-service'] = userInfo.service;
    if (userInfo.language) config.headers['x-user-language'] = userInfo.language;
    if (userInfo.id) config.headers['x-user-id'] = String(userInfo.id);
  }

  if (bearerToken) {
    config.headers['Authorization'] = `Bearer ${bearerToken}`;
  }
};

// ─── Clean instance (no auth, no interceptors) ───────────────────────────────
export const cleanAPI = axios.create({ baseURL: API_URL });

// ─── Authenticated instance ──────────────────────────────────────────────────
const api = axios.create();

api.interceptors.request.use(
  async (config) => {
    await setCommonHeaders(config);
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

/** Strapi refused to renew the session (bad, expired or already-used refresh token). */
const isRefreshRefused = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status;
  return status === 401 || status === 400;
};

// Single-flight refresh: concurrent 401s share one refresh promise.
let refreshPromise: Promise<string | null> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig | undefined;
    const status = error.response?.status;

    if (!originalRequest) return Promise.reject(error);

    // ─── 401 → refresh once, then retry ───
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = (async () => {
          const { refreshToken } = await getTokens();
          if (!refreshToken) return null;
          try {
            return await refreshAndStoreTokens(refreshToken);
          } catch (refreshError) {
            // refresh-transient-logout — only a refusal proves the session is dead.
            // Network, timeout, 5xx: keep the tokens, fail this request; the next
            // 401 retries (the back hands back a child lost after its rotation).
            if (isRefreshRefused(refreshError)) return null;
            throw refreshError;
          }
        })().finally(() => {
          refreshPromise = null;
        });
      }

      let newToken: string | null;
      try {
        newToken = await refreshPromise;
      } catch (transientError) {
        // Transient refresh failure: the caller gets the network / 5xx error, so the
        // UI shows it as one and Sentry reports it (handleApiError skips 401s).
        return Promise.reject(transientError);
      }

      if (newToken) {
        originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
        return api(originalRequest);
      }

      await forceLogout('refresh_failed_after_401');
      return Promise.reject(error);
    }

    // ─── 5xx → retry with backoff (max 2) ───
    if (status && status >= 500 && status < 600) {
      const retryCount = originalRequest._retryCount ?? 0;
      if (retryCount < 2) {
        originalRequest._retryCount = retryCount + 1;
        const delay = Math.min(1000 * 2 ** retryCount, 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(originalRequest);
      }
      Sentry.captureException(error, {
        tags: { error_type: '5xx_server_error', http_status: String(status) },
      });
    }

    return Promise.reject(error);
  },
);

export type { AxiosRequestConfig };
export default api;
